import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { BASE_URL } from "../apis/api";

const STORAGE_KEY = "vbot_app_version";
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_DELAY_MS = 2500;

const CLIENT_BUILD_ID = process.env.REACT_APP_BUILD_ID || null;

async function fetchServerVersion() {
  const url = `${BASE_URL}/app-version`;
  const response = await fetch(url, {
    credentials: "include",
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });

  if (!response.ok) {
    throw new Error(`Version check failed (${response.status})`);
  }

  const data = await response.json();
  return data?.data?.version || data?.version || null;
}

function scheduleReload() {
  toast.success("A new version is available. Updating...", {
    duration: RELOAD_DELAY_MS,
  });

  window.setTimeout(() => {
    window.location.reload();
  }, RELOAD_DELAY_MS);
}

export function useAppVersionCheck() {
  const reloadScheduled = useRef(false);

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return undefined;
    }

    const handleMismatch = (serverVersion) => {
      if (!serverVersion || reloadScheduled.current) {
        return;
      }

      const knownVersion = localStorage.getItem(STORAGE_KEY);
      const currentVersion = CLIENT_BUILD_ID || knownVersion;

      if (!currentVersion) {
        localStorage.setItem(STORAGE_KEY, serverVersion);
        return;
      }

      if (currentVersion === serverVersion) {
        localStorage.setItem(STORAGE_KEY, serverVersion);
        return;
      }

      reloadScheduled.current = true;
      localStorage.setItem(STORAGE_KEY, serverVersion);
      scheduleReload();
    };

    const checkVersion = async () => {
      try {
        const serverVersion = await fetchServerVersion();
        handleMismatch(serverVersion);
      } catch {
        // Ignore transient network errors during background checks.
      }
    };

    checkVersion();

    const intervalId = window.setInterval(checkVersion, CHECK_INTERVAL_MS);
    const onFocus = () => {
      checkVersion();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    });

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
}
